import { z } from 'zod'

/**
 * Provenance d'un témoignage. Portée par la donnée elle-même, et pas déduite
 * de la présence d'un identifiant externe : c'est elle qui décidera de la
 * mention d'attribution à afficher (un avis Google doit être annoncé comme
 * tel), et elle doit rester lisible même si l'identifiant d'origine venait à
 * manquer.
 *
 * 'google' n'est écrit par personne aujourd'hui — l'import n'existe pas
 * encore. La valeur est déclarée dès maintenant parce qu'elle est la
 * charnière du plan en deux temps : la table, les routes et l'écran
 * d'administration sont les mêmes pour les deux sources, et la synchronisation
 * viendra écrire dans l'existant plutôt que de le migrer.
 */
export const TESTIMONIAL_SOURCES = ['manuel', 'google'] as const
export const testimonialSourceSchema = z.enum(TESTIMONIAL_SOURCES)
export type TestimonialSource = z.infer<typeof testimonialSourceSchema>

/**
 * État de modération. Trois valeurs et non un simple booléen `visible` (comme
 * les familles du catalogue) parce qu'il y a deux façons distinctes de ne pas
 * être publié, et que les confondre ferait perdre du travail :
 *
 * - 'en-attente' : jamais relu. C'est l'état d'arrivée des avis importés.
 * - 'masque' : relu puis écarté délibérément. Un import ne doit pas le
 *   ressusciter, alors qu'il peut légitimement re-proposer un 'en-attente'.
 */
export const TESTIMONIAL_STATUSES = ['publie', 'en-attente', 'masque'] as const
export const testimonialStatusSchema = z.enum(TESTIMONIAL_STATUSES)
export type TestimonialStatus = z.infer<typeof testimonialStatusSchema>

/**
 * Longueur maximale d'une citation. Bornée pour que la grille du site reste
 * lisible : au-delà, la carte de témoignage déborde son voisin. Les avis
 * Google dépassant cette longueur seront tronqués à l'import, jamais refusés.
 */
export const TESTIMONIAL_QUOTE_MAX = 600

/**
 * Le brouillon : ce qu'un formulaire envoie. Sans `id` ni `source`, qui
 * n'appartiennent pas à la saisie — l'un est attribué par la base, l'autre par
 * la route qui écrit ('manuel' depuis l'administration, 'google' depuis
 * l'import à venir). Un client ne peut donc pas faire passer une saisie
 * manuelle pour un avis Google.
 */
export const testimonialDraftSchema = z.object({
  authorName: z
    .string()
    .trim()
    .min(1, 'Le nom est obligatoire')
    .max(80, 'Le nom ne peut pas dépasser 80 caractères'),
  quote: z
    .string()
    .trim()
    .min(1, 'Le témoignage ne peut pas être vide')
    .max(TESTIMONIAL_QUOTE_MAX, `Le témoignage ne peut pas dépasser ${TESTIMONIAL_QUOTE_MAX} caractères`),
  // Nullable, et ce n'est pas un oubli : un témoignage recueilli de vive voix
  // ou par courriel n'a pas de note en étoiles, et lui en inventer une serait
  // exactement le genre de détail fabriqué que cet écran existe pour faire
  // disparaître. Voir temoignagesAffichables pour la conséquence côté filtre.
  rating: z
    .number()
    .int('La note doit être un nombre entier')
    .min(1, 'La note va de 1 à 5')
    .max(5, 'La note va de 1 à 5')
    .nullable(),
  status: testimonialStatusSchema,
})

export type TestimonialDraft = z.infer<typeof testimonialDraftSchema>

export const testimonialSchema = testimonialDraftSchema.extend({
  id: z.string().uuid(),
  source: testimonialSourceSchema,
})

export type Testimonial = z.infer<typeof testimonialSchema>

/** Réordonnancement : la liste complète des identifiants, dans l'ordre voulu. */
export const testimonialOrderSchema = z.object({
  ids: z.array(z.string().uuid()),
})

export type TestimonialOrder = z.infer<typeof testimonialOrderSchema>

/**
 * Les réglages de la section, tous appliqués à la lecture publique. Aucun
 * réglage « pour plus tard » ici : un bouton qui ne fait rien dans un
 * back-office tenu par des non-techniciens est pire qu'un bouton absent, on
 * finit par ne plus croire aucun des autres. Les seuils propres à l'import
 * (longueur minimale, publication automatique) arriveront avec l'import.
 */
export const testimonialsSettingsSchema = z.object({
  // Le commutateur de section. Prévu dès le premier jour : au déploiement,
  // l'institut n'aura peut-être encore aucun témoignage à montrer, et une
  // section vide vaut moins que pas de section du tout.
  enabled: z.boolean(),
  title: z.string().trim().min(1, 'Le titre de la section est obligatoire'),
  maxDisplayed: z
    .number()
    .int()
    .min(1, 'Il faut afficher au moins un témoignage')
    .max(12, 'Au-delà de 12, la section devient un mur illisible'),
  minRating: z
    .number()
    .int()
    .min(1, 'Le seuil va de 1 à 5 étoiles')
    .max(5, 'Le seuil va de 1 à 5 étoiles'),
})

export type TestimonialsSettings = z.infer<typeof testimonialsSettingsSchema>

export const DEFAULT_TESTIMONIALS_SETTINGS: TestimonialsSettings = {
  enabled: true,
  // Le titre déjà en place sur l'Accueil, repris à l'identique pour que le
  // basculement vers l'administration ne change pas un caractère de la page.
  title: 'Ce qu’elles et ils en disent',
  // Trois : le nombre de témoignages que la grille de l'Accueil affiche
  // aujourd'hui.
  maxDisplayed: 3,
  minRating: 4,
}

/** Ce que le site reçoit pour un témoignage : ni son statut, ni sa position. */
export type PublicTestimonial = Pick<Testimonial, 'id' | 'authorName' | 'quote' | 'rating' | 'source'>

export type TestimonialsPayload = {
  settings: TestimonialsSettings
  items: PublicTestimonial[]
}

/**
 * Ce que le site affiche tant que l'API n'a pas répondu. Liste VIDE, et c'est
 * le cœur du sujet : la page ne doit jamais embarquer de témoignage écrit à la
 * compilation. Trois citations inventées y étaient codées en dur jusqu'ici —
 * or un avis attribué à une personne qui n'a jamais été cliente est une
 * pratique commerciale trompeuse (art. L121-2 et suivants du code de la
 * consommation), aggravée lorsqu'elle passe par un support numérique.
 *
 * Section absente plutôt que peuplée de faux : même parti que l'emplacement
 * « Soins signature » de l'Accueil, qui n'existe que si une vraie photo y a
 * été placée.
 */
export const DEFAULT_TESTIMONIALS_PAYLOAD: TestimonialsPayload = {
  settings: DEFAULT_TESTIMONIALS_SETTINGS,
  items: [],
}
